import { type Module } from "vuex";
import type {
  DefaultApi,
  IntegrationRequest,
  IntegrationResponse,
} from "@/api";
import { Integration } from "@/models/integration";
import { Integrations } from "@/models/integrations";
import { KomboTool } from "@/models/kombo_tool";
import type { State as RootState } from "@/stores";
export interface State {
  integrations?: Integrations;
  loaded: boolean;
}
const pendingIntegrations = new WeakMap<State, Promise<Integrations>>();

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    integrations: undefined,
    loaded: false,
  },
  actions: {
    async getIntegrations({ state, commit }) {
      if (state.loaded) return state.integrations;
      const pending = pendingIntegrations.get(state);
      if (pending) return pending;

      const request = Promise.resolve().then(async () => {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getIntegrations();
        const integrations = new Integrations(
          response.data.map(
            (integration: Partial<IntegrationResponse>) =>
              new Integration(integration),
          ),
        );
        if (pendingIntegrations.get(state) === request)
          commit("setIntegrations", integrations);
        return integrations;
      });
      pendingIntegrations.set(state, request);
      try {
        return await request;
      } finally {
        if (pendingIntegrations.get(state) === request)
          pendingIntegrations.delete(state);
      }
    },
    async postIntegration({ commit }, integrationRequest: IntegrationRequest) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postIntegration(integrationRequest);
      const integration = new Integration(response.data);
      commit("setIntegration", integration);
      return integration;
    },
    async postKomboInit({}, integrationTool?: string) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postKomboInit({
        integration_tool: integrationTool,
      });
      return response;
    },
    async postKomboActivate({ commit }, activationToken: string) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postKomboActivate({
        activation_token: activationToken,
      });
      commit("invalidateIntegrations");
      return response;
    },
    async getKomboTools() {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getKomboTools();
      return response.data.map(
        (tool: Partial<KomboTool>) => new KomboTool(tool),
      );
    },
  },
  mutations: {
    setIntegration(state, integration: Integration) {
      if (state.integrations === undefined) {
        state.integrations = new Integrations([integration]);
      } else {
        state.integrations.push(integration);
      }
      state.loaded = false;
      pendingIntegrations.delete(state);
    },
    setIntegrations(state, integrations: Integrations) {
      state.integrations = integrations;
      state.loaded = true;
    },
    invalidateIntegrations(state) {
      state.loaded = false;
      pendingIntegrations.delete(state);
    },
  },
};
